import { useEffect, useState } from 'react'
import { loadThemePreference, saveThemePreference, type ThemePreference } from '../storage'

/** Must match --color-bg per theme in src/styles/tokens.css, and the two media-scoped
 * `<meta name="theme-color">` tags in index.html. */
const THEME_COLORS: Record<Exclude<ThemePreference, 'system'>, string> = {
  light: '#f0efec',
  dark: '#0f0f13',
}

const THEME_COLOR_OVERRIDE_ID = 'theme-color-override'

/**
 * Installed, the app paints the OS status bar with `theme-color`, so it has to follow
 * the theme the user actually chose rather than the one the OS prefers.
 *
 * index.html carries a media-scoped pair (light/dark) that is right whenever the preference is
 * 'system'. An explicit choice needs to beat both, and a browser uses the *first* theme-color meta
 * whose media matches — so the override is inserted at the head's front rather than appended, and
 * removed again when the preference goes back to 'system'.
 */
function applyThemeColor(preference: ThemePreference) {
  const existing = document.getElementById(THEME_COLOR_OVERRIDE_ID)
  if (preference === 'system') {
    existing?.remove()
    return
  }
  const meta = existing ?? document.createElement('meta')
  meta.id = THEME_COLOR_OVERRIDE_ID
  meta.setAttribute('name', 'theme-color')
  meta.setAttribute('content', THEME_COLORS[preference])
  if (!existing) document.head.insertBefore(meta, document.head.firstChild)
}

function applyThemePreference(preference: ThemePreference) {
  if (preference === 'system') {
    document.documentElement.removeAttribute('data-theme')
  } else {
    document.documentElement.setAttribute('data-theme', preference)
  }
  applyThemeColor(preference)
}

export interface ThemeController {
  themePreference: ThemePreference
  changeTheme: (preference: ThemePreference) => void
}

/**
 * The preference is read synchronously on first render (index.html's inline
 * script has already applied it to avoid a flash), persisted to localStorage on change, and
 * mirrored onto `<html data-theme>` — 'system' means "remove the attribute and let the media
 * query decide".
 */
export function useThemePreference(): ThemeController {
  const [themePreference, setThemePreference] = useState<ThemePreference>(() =>
    loadThemePreference()
  )

  useEffect(() => {
    applyThemePreference(themePreference)
  }, [themePreference])

  return {
    themePreference,
    changeTheme: (preference) => {
      saveThemePreference(preference)
      setThemePreference(preference)
    },
  }
}
